<script>
import { apiSecond } from '@/boot/axios'
import { mapGetters } from 'vuex'
import HistoryTabs from './components/HistoryTabs.vue'
import HistoryFilters from './components/HistoryFilters.vue'
import HistoryShipmentCard from './components/HistoryShipmentCard.vue'
import HistoryAnalytics from './components/HistoryAnalytics.vue'

export default {
	name: 'VirtualWarehouseHistory',
	components: {
		HistoryTabs,
		HistoryFilters,
		HistoryShipmentCard,
		HistoryAnalytics,
	},
	data() {
		return {
			loading: false,
			errorMessage: null,
			items: [],
			activeTab: 'shipments',
			filters: {
				searchNikad: '',
				searchBin: '',
				status: null,
				direction: null,
				dateFrom: '',
				dateTo: '',
			},
			statusOptions: [
				{ id: 'pending', name: this.$t('virtual_warehouse.history.status_map.pending') },
				{ id: 'current', name: this.$t('virtual_warehouse.history.status_map.current') },
				{ id: 'completed', name: this.$t('virtual_warehouse.history.status_map.completed') },
				{ id: 'denied', name: this.$t('virtual_warehouse.history.status_map.denied') },
			],
		}
	},
	computed: {
		...mapGetters(['_getUserObject']),
		unit() {
			return this.$t('virtual_warehouse.history.unit_kg')
		},
		tabs() {
			return [
				{ id: 'shipments', name: this.$t('virtual_warehouse.history.tab_shipments') },
				{ id: 'analytics', name: this.$t('virtual_warehouse.history.tab_analytics') },
			]
		},
		directionOptions() {
			return [
				{ id: 'out', name: this.$t('virtual_warehouse.history.direction_out') },
				{ id: 'in', name: this.$t('virtual_warehouse.history.direction_in') },
			]
		},
		// Raw items mapped to a normalized shape consumed by the card / analytics
		normalizedItems() {
			return this.items.map((item) => this.normalizeItem(item))
		},
		displayedItems() {
			let result = [...this.normalizedItems]

			if (this.filters.searchNikad && this.filters.searchNikad.trim()) {
				const term = this.filters.searchNikad.toLowerCase()
				result = result.filter(
					(i) =>
						(i.nikad || '').toLowerCase().includes(term) ||
						(i.refNikad || '').toLowerCase().includes(term),
				)
			}
			if (this.filters.searchBin && this.filters.searchBin.trim()) {
				const term = this.filters.searchBin.toLowerCase()
				result = result.filter(
					(i) =>
						(i.sender.bin || '').toLowerCase().includes(term) ||
						(i.receiver.bin || '').toLowerCase().includes(term),
				)
			}
			if (this.filters.status) {
				result = result.filter((i) => i.rawStatus === this.filters.status.id)
			}
			if (this.filters.direction) {
				result = result.filter((i) => i.direction === this.filters.direction.id)
			}
			if (this.filters.dateFrom) {
				result = result.filter((i) => i.rawDate && i.rawDate >= this.filters.dateFrom)
			}
			if (this.filters.dateTo) {
				result = result.filter((i) => i.rawDate && i.rawDate <= this.filters.dateTo)
			}
			return result
		},
	},
	mounted() {
		this.fetchHistory()
	},
	methods: {
		async fetchHistory() {
			this.loading = true
			this.errorMessage = null
			try {
				const { data } = await apiSecond.get('/v1/virtual-store/history-data')
				this.items = Array.isArray(data?.data) ? data.data : []
			} catch (error) {
				this.items = []
				this.errorMessage = this.resolveErrorMessage(error)
			} finally {
				this.loading = false
			}
		},
		resolveErrorMessage(error) {
			// No response at all → network / timeout / CORS
			if (!error || !error.response) {
				return this.$t('virtual_warehouse.toast.network_error')
			}
			const status = error.response.status
			const serverMsg = error.response.data && error.response.data.message
			if (status === 401) {
				// Session expired — send the user back to login (same behaviour as the rest of the app)
				this.$router.push('/' + this.$i18n.locale + '/login').catch(() => {})
				return this.$t('virtual_warehouse.toast.auth_required')
			}
			if (status === 403) return this.$t('virtual_warehouse.toast.insufficient_permissions')
			if (status === 404) return this.$t('system_message.no_data')
			if (status === 400 || status === 422) {
				return serverMsg || this.$t('virtual_warehouse.toast.invalid_data')
			}
			if (status >= 500) return serverMsg || this.$t('system_message.500')
			return serverMsg || this.$t('virtual_warehouse.toast.send_error')
		},
		search() {
			// client-side filter only; computed reacts to filter changes
		},
		clearFilters() {
			this.filters = {
				searchNikad: '',
				searchBin: '',
				status: null,
				direction: null,
				dateFrom: '',
				dateTo: '',
			}
		},
		formatStatus(status) {
			const key = (status || '').toString().toLowerCase().trim()
			return this.$t(`virtual_warehouse.history.status_map.${key}`)
		},
		statusVariant(status) {
			const s = (status || '').toString().toLowerCase().trim()
			if (['completed', 'done', 'success', 'finished', 'accepted', 'accept'].includes(s)) {
				return 'green'
			}
			if (['current', 'active', 'in_progress', 'in progress', 'ongoing'].includes(s)) {
				return 'blue'
			}
			if (['pending', 'waiting', 'queued', 'new'].includes(s)) {
				return 'orange'
			}
			if (['denied', 'rejected', 'cancelled', 'sended', 'anullate'].includes(s)) {
				return 'red'
			}
			return 'grey'
		},
		getDirection(item) {
			// Backend provides direction explicitly ("outgoing" / "incoming")
			const dir = (item.direction || '').toString().toLowerCase()
			if (dir === 'incoming' || dir === 'in') return 'in'
			if (dir === 'outgoing' || dir === 'out') return 'out'
			// Fallback: derive from which party is the current user
			const senderBin =
				item.sender_bin || item?.sender?.iin_bin || item?.user_nikad_agro_culture?.bin
			const receiverBin = item.receiver_bin || item?.receiver?.iin_bin || item.iin_bin_receiver
			const myBin = this._getUserObject?.iin_bin
			if (myBin && receiverBin === myBin) return 'in'
			if (myBin && senderBin === myBin) return 'out'
			return 'out'
		},
		formatDate(value) {
			if (!value) return ''
			const d = new Date(value)
			if (isNaN(d.getTime())) return ('' + value).slice(0, 10)
			const y = d.getFullYear()
			const m = String(d.getMonth() + 1).padStart(2, '0')
			const day = String(d.getDate()).padStart(2, '0')
			return `${y}-${m}-${day}`
		},
		resolveName(name, bin) {
			return name || bin || '-'
		},
		toNumber(value) {
			const n = Number(value)
			return isFinite(n) ? n : 0
		},
		normalizeItem(item) {
			const senderBin =
				item.sender_bin || item?.sender?.iin_bin || item?.user_nikad_agro_culture?.bin || ''
			const receiverBin =
				item.receiver_bin || item?.receiver?.iin_bin || item.iin_bin_receiver || ''

			const senderName = item?.sender?.name_company || item?.sender?.fio || item.sender_name || ''
			const receiverName =
				item?.receiver?.name_company || item?.receiver?.fio || item.receiver_name || ''

			const fishes = Array.isArray(item.fishes) ? item.fishes : []
			const total =
				Math.round(fishes.reduce((acc, f) => acc + this.toNumber(f.size), 0) * 100) / 100

			const nikad = item.nikad || item?.user_nikad_agro_culture?.nikad || ''
			const refNikad = item.reference_nikad || ''
			const rawStatus = (item.status || '').toString().toLowerCase().trim()
			const rawDate = this.formatDate(item.created_at || item.issue_date || item.date)
			const direction = this.getDirection(item)

			return {
				id: item.id,
				nikad,
				refNikad: refNikad && refNikad !== nikad ? refNikad : '',
				date: rawDate,
				rawDate,
				rawStatus,
				sender: { name: this.resolveName(senderName, senderBin), bin: senderBin },
				receiver: { name: this.resolveName(receiverName, receiverBin), bin: receiverBin },
				fishes,
				total,
				statusVariant: this.statusVariant(rawStatus),
				statusLabel: this.formatStatus(rawStatus),
				direction,
				directionLabel:
					direction === 'in'
						? this.$t('virtual_warehouse.history.direction_in')
						: this.$t('virtual_warehouse.history.direction_out'),
			}
		},
	},
}
</script>

<template>
	<div class="container">
		<div class="row">
			<div class="col-xl-2 col-lg-3">
				<v-sidebar :active="['VirtualWarehouseMain', 'VirtualWarehouseHistory']"></v-sidebar>
			</div>
			<div class="col-xl-10 col-lg-9">
				<div class="content-wrapper">
					<div class="card__content">
						<div class="history">
							<div class="history__heading">
								<div>
									<div class="history__title">{{ $t('virtual_warehouse.history.title') }}</div>
									<div class="history__subtitle">
										{{ $t('virtual_warehouse.history.subtitle') }}
									</div>
								</div>
								<button
									class="history__help"
									type="button"
									:title="$t('virtual_warehouse.help.history_title')"
									@click="$modal.show('modal_help_history')"
								>
									?
								</button>
							</div>

							<history-tabs v-model="activeTab" :tabs="tabs" />

							<history-filters
								:filters.sync="filters"
								:status-options="statusOptions"
								:direction-options="directionOptions"
								@search="search"
								@clear="clearFilters"
							/>

							<v-alert v-if="errorMessage" type="error" class="history__alert">
								<div class="history__alert-row">
									<span>{{ errorMessage }}</span>
									<button class="history__retry" type="button" @click="fetchHistory">
										{{ $t('commands.update') }}
									</button>
								</div>
							</v-alert>

							<div v-if="activeTab === 'shipments'" class="history__list">
								<div v-if="loading" class="history__state">
									<v-progress-circular indeterminate color="#157fee" />
								</div>
								<div v-else-if="!displayedItems.length" class="history__state">
									{{ $t('virtual_warehouse.history.empty') }}
								</div>
								<template v-else>
									<history-shipment-card
										v-for="item in displayedItems"
										:key="item.id"
										:item="item"
										:unit="unit"
									/>
								</template>
							</div>

							<history-analytics v-else :items="displayedItems" :unit="unit" />

							<!-- Help Modal: History -->
							<modal
								class="modal__block modal__block--middle modal__help"
								name="modal_help_history"
								:width="700"
								:min-width="600"
								height="auto"
							>
								<div class="modal__block--wrapper">
									<div class="modal__block--close" @click="$modal.hide('modal_help_history')">
										<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M22.5 7.5l-15 15M7.5 7.5l15 15" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
									</div>
									<div class="modal__wrapper">
										<div class="modal__header">
											<div class="modal__title">
												{{ $t('virtual_warehouse.help.history_title') }}
											</div>
										</div>
										<div class="modal__content">
											<p class="history-text">{{ $t('virtual_warehouse.help.history_text') }}</p>
											<div class="help-actions">
												<a
													class="block__item block__item--blue"
													@click="$modal.hide('modal_help_history')"
													>{{ $t('button.ok') }}</a
												>
											</div>
										</div>
									</div>
								</div>
							</modal>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.history {
	padding: 4px 0;
}

.history__heading {
	position: relative;
	padding-left: 16px;
	margin-bottom: 20px;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
}

.history__help {
	flex-shrink: 0;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	border: none;
	background: #eaf3ff;
	color: #157fee;
	font-weight: 700;
	font-size: 14px;
	cursor: pointer;
	transition: background-color 0.2s, color 0.2s;
}
.history__help:hover {
	background: #157fee;
	color: #ffffff;
}

.history-text {
	color: #333;
	line-height: 1.5;
	margin: 0;
}
.help-actions {
	display: flex;
	justify-content: flex-end;
	margin-top: 10px;
}
.modal__help .modal__header {
	padding: 12px 16px;
}
.modal__help .modal__content {
	padding: 12px 16px 10px 16px;
}
.modal__help .modal__block--wrapper {
	border-radius: 10px;
}

.history__heading::before {
	content: '';
	position: absolute;
	left: 0;
	top: 2px;
	bottom: 2px;
	width: 4px;
	border-radius: 4px;
	background: #157fee;
}

.history__title {
	font-family: 'Montserrat', sans-serif;
	font-size: 18px;
	font-weight: 600;
	letter-spacing: 0;
	text-transform: uppercase;
	color: #151d48;
	line-height: 1.2;
}

.history__subtitle {
	font-family: 'Montserrat', sans-serif;
	margin-top: 5px;
	font-size: 14px;
	font-weight: 400;
	letter-spacing: 0;
	line-height: 1.2;
	color: #475f84;
}

.history__alert {
	margin-top: 16px;
}
.history__alert-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
}
.history__retry {
	flex-shrink: 0;
	border: 1px solid #ffffff;
	background: transparent;
	color: #ffffff;
	font-size: 13px;
	font-weight: 600;
	padding: 6px 14px;
	border-radius: 8px;
	cursor: pointer;
}
.history__retry:hover {
	background: rgba(255, 255, 255, 0.15);
}

.history__list {
	margin-top: 22px;
}

.history__state {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 60px 0;
	color: #8c9bb5;
	font-size: 15px;
}
</style>
